import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { User, Mail, Shield, CheckCircle, XCircle } from 'lucide-react';

const AuthTester = () => {
  const { user, login, signup, logout, resetPassword, isLoading } = useAuth();
  const [testResults, setTestResults] = useState<{ [key: string]: boolean | null }>({});
  const [testLogs, setTestLogs] = useState<string[]>([]);

  const addLog = (message: string) => {
    setTestLogs(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  const testAuth = async () => {
    setTestResults({});
    setTestLogs([]);
    addLog('Starting authentication tests...');

    // Test 1: Signup with test user
    addLog('Test 1: Testing user signup...');
    const testEmail = `test+${Date.now()}@example.com`;
    const testPassword = 'testpassword123';
    const testName = 'Test User';

    try {
      const signupResult = await signup(testName, testEmail, testPassword);
      if (signupResult.success) {
        addLog('✅ Signup test passed');
        setTestResults(prev => ({ ...prev, signup: true }));
      } else {
        addLog(`❌ Signup test failed: ${signupResult.error}`);
        setTestResults(prev => ({ ...prev, signup: false }));
      }
    } catch (error: any) {
      addLog(`❌ Signup test error: ${error.message}`);
      setTestResults(prev => ({ ...prev, signup: false }));
    }

    // Test 2: Login (if signup was successful)
    if (testResults.signup) {
      addLog('Test 2: Testing user login...');
      try {
        const loginResult = await login(testEmail, testPassword);
        if (loginResult.success) {
          addLog('✅ Login test passed');
          setTestResults(prev => ({ ...prev, login: true }));
        } else {
          addLog(`❌ Login test failed: ${loginResult.error}`);
          setTestResults(prev => ({ ...prev, login: false }));
        }
      } catch (error: any) {
        addLog(`❌ Login test error: ${error.message}`);
        setTestResults(prev => ({ ...prev, login: false }));
      }
    }

    // Test 3: Reset password
    addLog('Test 3: Testing password reset...');
    try {
      const resetResult = await resetPassword(testEmail);
      if (resetResult.success) {
        addLog('✅ Password reset test passed');
        setTestResults(prev => ({ ...prev, resetPassword: true }));
      } else {
        addLog(`❌ Password reset test failed: ${resetResult.error}`);
        setTestResults(prev => ({ ...prev, resetPassword: false }));
      }
    } catch (error: any) {
      addLog(`❌ Password reset test error: ${error.message}`);
      setTestResults(prev => ({ ...prev, resetPassword: false }));
    }

    // Test 4: Logout (if user is logged in)
    if (user) {
      addLog('Test 4: Testing logout...');
      try {
        await logout();
        addLog('✅ Logout test passed');
        setTestResults(prev => ({ ...prev, logout: true }));
      } catch (error: any) {
        addLog(`❌ Logout test error: ${error.message}`);
        setTestResults(prev => ({ ...prev, logout: false }));
      }
    }

    addLog('All tests completed!');
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <Card className="card-elegant">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-6 w-6 text-primary" />
            Authentication System Test
          </CardTitle>
          <CardDescription>
            Test the complete authentication flow including signup, login, and password reset
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Current User Status */}
          <div className="flex items-center gap-4 p-4 bg-muted/30 rounded-lg">
            <User className="h-5 w-5 text-primary" />
            <div className="flex-1">
              <p className="font-medium">Current User Status</p>
              <p className="text-sm text-muted-foreground">
                {user ? `Logged in as ${user.email} (${user.name})` : 'Not logged in'}
              </p>
            </div>
            <Badge variant={user ? "default" : "secondary"}>
              {user ? 'Authenticated' : 'Guest'}
            </Badge>
          </div>

          {/* Test Button */}
          <Button 
            onClick={testAuth} 
            disabled={isLoading}
            className="w-full btn-hero"
          >
            {isLoading ? 'Testing...' : 'Run Authentication Tests'}
          </Button>

          {/* Test Results */}
          {Object.keys(testResults).length > 0 && (
            <div className="space-y-2">
              <h3 className="font-semibold">Test Results:</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {Object.entries(testResults).map(([test, result]) => (
                  <div key={test} className="flex items-center gap-2 p-2 bg-muted/30 rounded">
                    {result === true ? (
                      <CheckCircle className="h-4 w-4 text-success" />
                    ) : result === false ? (
                      <XCircle className="h-4 w-4 text-destructive" />
                    ) : (
                      <div className="h-4 w-4 rounded-full bg-muted" />
                    )}
                    <span className="text-sm capitalize">{test.replace(/([A-Z])/g, ' $1')}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Test Logs */}
          {testLogs.length > 0 && (
            <div className="space-y-2">
              <h3 className="font-semibold">Test Logs:</h3>
              <div className="max-h-48 overflow-y-auto p-3 bg-muted/30 rounded text-sm font-mono space-y-1">
                {testLogs.map((log, index) => (
                  <div key={index} className="text-xs">
                    {log}
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* User Profile Card (if logged in) */}
      {user && (
        <Card className="card-elegant">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-primary" />
              User Profile
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between">
              <span className="text-muted-foreground">ID:</span>
              <span className="font-mono text-sm">{user.id}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Name:</span>
              <span>{user.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Email:</span>
              <span>{user.email}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Plan:</span>
              <Badge variant="outline">{user.plan}</Badge>
            </div>

            <Button 
              onClick={logout} 
              variant="outline" 
              className="w-full mt-4"
            >
              Logout
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AuthTester;